import * as Sentry from '@sentry/react';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { queryKeys, useGetPdpQuery } from '@vizit/api';
import { useAuth } from '@vizit/auth';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { ASSETS_FILTER_SOURCES } from '@vizit/organisms';
import type { AssetsFilterSource } from '@vizit/organisms';
import { ViewMode } from '@vizit/products';
import type { BreadcrumbItem } from '@vizit/utils';
import { buildDynamicBreadcrumbs } from '@vizit/utils';
import * as React from 'react';
import { resolveLoaderWorkspaceId, useActiveWorkspaceId } from '../../../../../hooks/use-workspace-link';
import { PagesProvider } from '../../../../../pages';
import { ProductDetailsPage } from '../../../../../pages/components';
import { LIBRARY_BREADCRUMB, beforeLoadAuthenticatedValidation } from '../../../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../../../templates';

type ProductDetailsSearch = {
  products?: 'open' | 'collapsed';
  tab?: 'hero' | 'carousel';
  carouselTab?: AssetsFilterSource;
  heroTab?: AssetsFilterSource;
  heroFav?: 'only';
  carouselFav?: 'only';
  view?: ViewMode;
};

function asAssetsFilterSource(value: unknown): AssetsFilterSource | undefined {
  return ASSETS_FILTER_SOURCES.includes(value as AssetsFilterSource) ? (value as AssetsFilterSource) : undefined;
}

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/library/products/$pdpId')({
  staticData: {
    pageName: 'Product Details',
  },
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  component: RouteComponent,
  loader: async ({ location, params, context }) => {
    const organizationId = context.auth?.organizationId;
    const workspaceId = resolveLoaderWorkspaceId(context, params.workspaceSlug);

    let productName = 'Product Details';

    if (organizationId && workspaceId) {
      try {
        const pdpData = await context.queryClient.ensureQueryData({
          queryKey: queryKeys.products.detail({
            organization_id: organizationId,
            workspace_id: workspaceId,
            pdp_id: params.pdpId,
          }).queryKey,
          queryFn: () =>
            useGetPdpQuery.fetcher({
              organization_id: organizationId,
              workspace_id: workspaceId,
              pdp_id: params.pdpId,
            })(),
        });

        productName = pdpData?.name ?? 'Product Details';
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'product details',
          },
        });
      }
    }

    const breadcrumbs = buildDynamicBreadcrumbs(
      location.state?.from,
      productName,
      // Current page is not clickable, so we don't need to pass href
      undefined,
      location.search,
      LIBRARY_BREADCRUMB,
    );

    return { breadcrumbs };
  },
  validateSearch: (search: Record<string, unknown>): ProductDetailsSearch => {
    const products = search.products === 'open' || search.products === 'collapsed' ? search.products : undefined;
    const tab = search.tab === 'hero' || search.tab === 'carousel' ? search.tab : undefined;
    const carouselTab = asAssetsFilterSource(search.carouselTab);
    const heroTab = asAssetsFilterSource(search.heroTab);
    const view = search.view === ViewMode.ORIGINAL ? search.view : undefined;
    const heroFav = search.heroFav === 'only' ? 'only' : undefined;
    const carouselFav = search.carouselFav === 'only' ? 'only' : undefined;
    return { products, tab, carouselTab, heroTab, heroFav, carouselFav, view };
  },
});

/**
 * On a hard refresh the loader can run before auth hydrates, so it skips the
 * product-name fetch and labels the current crumb "Product Details". This
 * re-resolves the name reactively (sharing the loader's query cache — no
 * extra request when it already ran) and corrects the crumb once it arrives.
 */
function useProductNameBreadcrumbs(pdpId: string): BreadcrumbItem[] | undefined {
  const { breadcrumbs } = Route.useLoaderData();
  const { organizationId } = useAuth();
  const workspaceId = useActiveWorkspaceId();

  const { data } = useGetPdpQuery(
    { organization_id: organizationId ?? '', workspace_id: workspaceId ?? '', pdp_id: pdpId },
    { enabled: !!organizationId && !!workspaceId },
  );

  return React.useMemo(() => {
    const name = data?.name;
    const current = breadcrumbs.at(-1);
    if (!name || !current || current.href || current.label === name) {
      return undefined;
    }
    return [...breadcrumbs.slice(0, -1), { ...current, label: name }];
  }, [breadcrumbs, data?.name]);
}

function RouteComponent() {
  const { pdpId } = useParams({ from: Route.id });
  const search = useSearch({ from: Route.id });
  const productNameBreadcrumbs = useProductNameBreadcrumbs(pdpId);

  return (
    <PagesProvider template={BreadcrumbsPageTemplate} templateProps={{ breadcrumbs: productNameBreadcrumbs }}>
      <ProductDetailsPage
        pdpId={pdpId}
        drawerState={search.products}
        activeTab={search.tab}
        carouselSubTab={search.carouselTab}
        heroSubTab={search.heroTab}
        heroFavoritesOnly={search.heroFav === 'only'}
        carouselFavoritesOnly={search.carouselFav === 'only'}
        viewMode={search.view}
      />
    </PagesProvider>
  );
}
